import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Loading } from '../../../../global/Loading/Loading'
import { Badge } from '../../../../global/badge/Badge'
import { Button } from '../../../../global/button/Button'
import { FilterQueryProps } from '../../../../global/filter/FilterQueryModal'
import { CheckBoxActiveIcon } from '../../../../icons/CheckBoxActiveIcon'
import { CheckBoxInactiveIcon } from '../../../../icons/CheckBoxInactiveIcon'
import { CircleCloseIcon } from '../../../../icons/CircleCloseIcon'
import { NavigationArrowDownIcon } from '../../../../icons/NavigationArrowDownIcon'
import { NameDto } from '../../../../model/NameDto'
import { useWindowResize } from '../../../../utils/domUtils'
import { Dropdown } from '../../../v2/Dropdown/Dropdown'
import { DropdownCard } from '../../../v2/Dropdown/DropdownCard'
import { DropdownList } from '../../../v2/Dropdown/DropdownList'
import { DropdownListItem } from '../../../v2/Dropdown/DropdownListItem'
import { DropdownSearchInput } from '../../../v2/Dropdown/DropdownSearchInput'
import { InputSearch } from '../../InputSearch/InputSearch'
import { FilterOptions } from '../TableFilter'

import styles from './FilterChipDropdownList.module.scss'

interface FilterChipDropdownListProps {
  name: string
  options?: NameDto<string | number>[]
  state?: FilterQueryProps
  filterType: FilterOptions
  isMultiSelect?: boolean
  hasSearch?: boolean
  isLoading?: boolean
  onSelect?: (value?: FilterQueryProps) => void
  onSearchChange?: (value: string) => void
  addButtonLabelMobile?: string
}

export const FilterChipDropdownList: FC<FilterChipDropdownListProps> = (props) => {
  const { t } = useTranslation()
  const {
    options = [],
    name,
    hasSearch,
    onSelect,
    onSearchChange,
    isMultiSelect,
    filterType,
    state,
    addButtonLabelMobile,
    isLoading,
  } = props

  const isMobile = useWindowResize()

  const [searchValue, setSearchValue] = useState<string>('')
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false)

  const isAllOptionsSelected = useMemo<boolean>(() => {
    if (!isMultiSelect) {
      return false
    }

    const selectedOptions = state?.[filterType]?.toString().split(' ') || []
    return (
      (!onSearchChange &&
        options?.every((option) => selectedOptions.includes(option.id.toString()))) ||
      false
    )
  }, [options, state, filterType, isMultiSelect, onSearchChange])

  const filteredOptions = useMemo<NameDto<string | number>[]>(() => {
    if (onSearchChange) {
      return options || []
    }

    return (
      options?.filter((option) => {
        return option.name.toLowerCase().includes(searchValue.toLowerCase())
      }) || []
    )
  }, [options, searchValue, onSearchChange])

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchValue(value)
      onSearchChange?.(value)
    },
    [setSearchValue, onSearchChange]
  )

  const handleToggleDropdown = useCallback(
    (dropdownState: boolean) => {
      setIsDropdownOpen(dropdownState)

      if (!dropdownState) {
        if (searchValue !== '') {
          handleSearchChange('')
        }
      }
    },
    [searchValue, setIsDropdownOpen, handleSearchChange]
  )

  const handleDropdownSelect = (value?: FilterQueryProps) => {
    onSelect?.(value)

    if (!isMultiSelect) {
      handleToggleDropdown(false)
    }
  }

  const handleModalSelect = (value?: FilterQueryProps) => {
    onSelect?.(value)
    handleToggleDropdown(false)
  }

  useEffect(() => {
    if (isAllOptionsSelected) {
      handleToggleDropdown(false)
    }
  }, [isAllOptionsSelected, handleToggleDropdown])

  if (options.length === 0 && (!hasSearch || !searchValue.trim())) {
    return
  }

  return (
    <Dropdown
      direction='topLeft'
      isOpen={isDropdownOpen}
      isMultiSelect={isMultiSelect}
      setIsOpen={handleToggleDropdown}
      content={
        isMobile ? (
          <MobileModalContent
            state={state}
            filterType={filterType}
            onSelect={handleModalSelect}
            hasSearch={hasSearch}
            searchValue={searchValue}
            setSearchValue={handleSearchChange}
            filteredOptions={filteredOptions}
            onClose={() => handleToggleDropdown(false)}
            addButtonLabel={addButtonLabelMobile}
            isMultiSelect={isMultiSelect}
          />
        ) : (
          <DropdownCard>
            {hasSearch && (
              <DropdownSearchInput
                placeholder={t('Search')}
                value={searchValue}
                onClear={() => handleSearchChange('')}
                onChange={(event) => handleSearchChange(event.target.value)}
              />
            )}
            <ListContent
              state={state}
              filterType={filterType}
              isMultiSelect={isMultiSelect}
              hasSearch={hasSearch}
              filteredOptions={filteredOptions}
              onSelect={handleDropdownSelect}
              isLoading={isLoading}
              hasDivider
            />
          </DropdownCard>
        )
      }
    >
      <Button.New
        renderRightIcon={() => <NavigationArrowDownIcon />}
        appearance='secondary'
        size='XS'
        className={styles.filterChip}
        onClick={() => handleToggleDropdown(true)}
      >
        <div className={styles.filterChip}>
          {name}
          {state?.[filterType] && (
            <Badge.New
              key={`${filterType}-${state?.[filterType]}`}
              text={state?.[filterType]?.toString().split(' ').length.toString() || ''}
              color='blue'
              size='small'
              rightIcon={CircleCloseIcon}
              onRightIconClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
                onSelect?.(state[filterType] ? { ...state, [filterType]: undefined } : state)
              }}
            />
          )}
        </div>
      </Button.New>
    </Dropdown>
  )
}

interface MobileModalContentProps {
  state?: FilterQueryProps
  filterType: FilterOptions
  onSelect: (value?: FilterQueryProps) => void
  onClose: () => void
  hasSearch?: boolean
  searchValue: string
  setSearchValue: (value: string) => void
  filteredOptions: NameDto<string | number>[]
  addButtonLabel?: string
  isMultiSelect?: boolean
  isLoading?: boolean
}

const MobileModalContent: FC<MobileModalContentProps> = (props) => {
  const { t } = useTranslation()

  const {
    state,
    filterType,
    hasSearch,
    searchValue,
    isLoading,
    setSearchValue,
    onSelect,
    filteredOptions,
    addButtonLabel = t('Apply'),
    isMultiSelect,
    onClose,
  } = props

  const [temporaryState, setTemporaryState] = useState<FilterQueryProps | undefined>(state)

  const currentValue = temporaryState?.[filterType] || ''
  const providedValue = state?.[filterType] || ''

  const handleSelect = (value: FilterQueryProps) => {
    setTemporaryState(value)
  }

  const handleAdd = () => {
    onSelect?.(temporaryState)
  }

  return (
    <DropdownCard className={styles.mobileModalContent}>
      <div className={styles.search}>
        {hasSearch && (
          <InputSearch
            isFullWidth
            isAutoFocus
            placeholder={t('Search')}
            value={searchValue}
            onClear={() => setSearchValue('')}
            onChange={(event) => setSearchValue(event.target.value)}
          />
        )}
      </div>
      <div className={styles.list}>
        <ListContent
          state={temporaryState}
          filterType={filterType}
          hasSearch={hasSearch}
          filteredOptions={filteredOptions}
          onSelect={handleSelect}
          isMultiSelect={isMultiSelect}
          isLoading={isLoading}
        />
      </div>
      <div className={styles.footer}>
        <Button.New
          appearance='primary'
          size='L'
          onClick={() => handleAdd()}
          disabled={currentValue === providedValue}
        >
          {addButtonLabel}
        </Button.New>
        <Button.New appearance='secondary' size='L' onClick={() => onClose()}>
          {t('Cancel')}
        </Button.New>
      </div>
    </DropdownCard>
  )
}

interface ListContentProps {
  filteredOptions?: NameDto<string | number>[]
  state?: FilterQueryProps
  filterType: FilterOptions
  isMultiSelect?: boolean
  hasSearch?: boolean
  onSelect?: (value: FilterQueryProps) => void
  addButtonLabelMobile?: string
  hasDivider?: boolean
  isLoading?: boolean
}

export const ListContent: FC<ListContentProps> = (props) => {
  const {
    state,
    filterType,
    hasSearch,
    isMultiSelect,
    filteredOptions,
    onSelect,
    hasDivider,
    isLoading,
  } = props

  const handleItemClick = (option: NameDto<string | number>) => {
    const currentSelection = state?.[filterType]?.toString().split(' ') || []
    const isOptionSelected = currentSelection.includes(option.id.toString())

    const newState = isMultiSelect
      ? {
          ...state,
          [filterType]: isOptionSelected
            ? (() => {
                const updatedSelection = currentSelection.filter(
                  (id: string) => id !== option.id.toString()
                )
                return updatedSelection.length ? updatedSelection.join(' ') : undefined
              })()
            : [...currentSelection, option.id].join(' '),
        }
      : {
          ...state,
          [filterType]: state?.[filterType] === option.id ? undefined : option.id,
        }

    onSelect?.(newState)
  }

  return (
    <DropdownList
      isScrollable={hasSearch}
      hasDivider={hasDivider}
      variant='compact'
      onMouseOver={(e) => e.stopPropagation()}
    >
      <Loading isLoading={!!isLoading} showLoadingIcon className={styles.loading}>
        {filteredOptions?.map((option, index) => {
          const isActive = state?.[filterType]?.toString().split(' ').includes(option.id.toString())

          return (
            <DropdownListItem
              key={index}
              startIcon={isActive ? CheckBoxActiveIcon : CheckBoxInactiveIcon}
              isActive={isActive}
              onClick={() => handleItemClick(option)}
              variant='compact'
            >
              {option.name}
            </DropdownListItem>
          )
        })}
      </Loading>
    </DropdownList>
  )
}
